import React, { useState } from 'react';
import { Globe, MapPin, Users, Zap, Heart, Target, Award, ArrowRight, Building2, Briefcase, CheckCircle } from 'lucide-react';

export default function AboutUsPage() {
  const [activeTab, setActiveTab] = useState('mission');

  const AnimatedBackground = () => (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950 to-emerald-950" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" 
           style={{ animation: 'float 8s ease-in-out infinite' }} />
      <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl animate-pulse" 
           style={{ animation: 'float 10s ease-in-out infinite reverse' }} />
      <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl animate-pulse" 
           style={{ animation: 'float 12s ease-in-out infinite' }} />
    </div>
  );

  return (
    <div className="relative min-h-screen bg-black overflow-hidden">
      <AnimatedBackground />

      {/* Navigation Bar */}
      <nav className="relative z-40 sticky top-0 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3">
              <div className="text-white font-bold text-2xl bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">
                IeeeAssistant
              </div>
            </div>
            <button
              onClick={() => window.history.back()}
              className="text-white/90 hover:text-white transition text-sm font-medium"
            >
              Back
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 pb-32 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-6xl md:text-7xl font-black text-white mb-6 leading-tight">
            About <span className="relative inline-block">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">
                IeeeAssistant
              </span>
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-600/50 to-emerald-600/50 blur-2xl -z-10 animate-pulse" />
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto mb-8">
            Revolutionizing Academic Publishing Through Innovation and Expertise
          </p>
          <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full px-6 py-3">
            <Globe className="w-5 h-5 text-emerald-400" />
            <span className="text-white text-sm font-medium">Global Presence in Poland & India</span>
          </div>
        </div>
      </section>

      {/* Company Info Section */}
      <section className="relative py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: Building2,
                title: 'Parent Company',
                desc: 'Scopewit Sp.zo.o. Technologies',
                detail: 'Pvt. Ltd'
              },
              {
                icon: MapPin,
                title: 'Founded',
                desc: '2019',
                detail: 'Poland'
              },
              {
                icon: Globe,
                title: 'Expanded',
                desc: '2025',
                detail: 'India'
              }
            ].map((item, idx) => (
              <div
                key={idx}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition transform hover:-translate-y-2"
              >
                <div className="bg-gradient-to-br from-blue-500 to-emerald-500 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400 mb-2">
                  {item.desc}
                </p>
                <p className="text-white/60">{item.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Tabs */}
      <section className="relative py-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Tab Buttons */}
          <div className="flex flex-wrap gap-4 mb-12 justify-center">
            {[
              { id: 'mission', label: 'Our Mission', icon: Target },
              { id: 'vision', label: 'Our Vision', icon: Zap },
              { id: 'values', label: 'Our Values', icon: Heart }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-blue-600 to-emerald-600 text-white shadow-lg'
                    : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-12">
            {activeTab === 'mission' && (
              <div className="space-y-6 animate-fadeIn">
                <h2 className="text-4xl font-bold text-white mb-6">Our Mission</h2>
                <p className="text-lg text-white/80 leading-relaxed">
                  At <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">IeeeAsistant</span>, our mission is to democratize academic publishing by making professional IEEE paper creation accessible to researchers worldwide, regardless of their technical expertise.
                </p>
                <p className="text-lg text-white/80 leading-relaxed">
                  We believe that great research should not be hindered by formatting complexities. Our platform eliminates barriers to publication, allowing researchers to focus on their work rather than wrestling with LaTeX syntax and IEEE compliance requirements.
                </p>
                <div className="bg-gradient-to-r from-blue-500/20 to-emerald-500/20 border border-blue-500/20 rounded-2xl p-8 mt-8">
                  <p className="text-white font-semibold text-lg">
                    "Empowering researchers through technology to accelerate scientific discovery and knowledge sharing."
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'vision' && (
              <div className="space-y-6 animate-fadeIn">
                <h2 className="text-4xl font-bold text-white mb-6">Our Vision</h2>
                <p className="text-lg text-white/80 leading-relaxed">
                  We envision a future where academic publishing is frictionless, transparent, and inclusive. By 2030, we aim to be the leading platform enabling researchers globally to publish high-quality, IEEE-compliant papers without technical barriers.
                </p>
                <div className="space-y-4 mt-8">
                  {[
                    'Serve 1 million+ active researchers across 180+ countries',
                    'Generate 10 million+ professionally formatted research papers annually',
                    'Become the gold standard for AI-assisted academic publishing',
                    'Advance scientific collaboration through innovative features'
                  ].map((vision, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-emerald-500 flex items-center justify-center flex-shrink-0 mt-1">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-white/80 text-lg">{vision}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'values' && (
              <div className="space-y-6 animate-fadeIn">
                <h2 className="text-4xl font-bold text-white mb-6">Our Core Values</h2>
                <div className="grid md:grid-cols-2 gap-6 mt-8">
                  {[
                    {
                      title: 'Excellence',
                      desc: 'We are committed to delivering the highest quality standards in everything we do, from product features to customer support.'
                    },
                    {
                      title: 'Innovation',
                      desc: 'We continuously push boundaries with cutting-edge AI technology and creative solutions to evolving challenges.'
                    },
                    {
                      title: 'Accessibility',
                      desc: 'We believe knowledge should be accessible to all. Our platform is designed to be user-friendly and affordable.'
                    },
                    {
                      title: 'Integrity',
                      desc: 'We operate with transparency and honesty, maintaining the highest ethical standards in data protection and service.'
                    },
                    {
                      title: 'Collaboration',
                      desc: 'We foster a culture of teamwork and partnership, believing great things happen when talented people work together.'
                    },
                    {
                      title: 'Impact',
                      desc: 'We measure success by the positive impact we create on research communities and the acceleration of scientific progress.'
                    }
                  ].map((value, idx) => (
                    <div key={idx} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition">
                      <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400 mb-3">
                        {value.title}
                      </h3>
                      <p className="text-white/70">{value.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Team & Expertise */}
      <section className="relative py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-white text-center mb-16">Our Team & Expertise</h2>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Global Team</h3>
                </div>
                <p className="text-white/70 leading-relaxed">
                  Our diverse team of software engineers, AI researchers, academic experts, and customer success specialists brings over 150 years of combined experience in technology and education.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Expertise</h3>
                </div>
                <p className="text-white/70 leading-relaxed">
                  We combine deep expertise in machine learning, academic publishing standards, user experience design, and enterprise software development.
                </p>
              </div>
            </div>

            <div className="space-y-8">
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-emerald-500 flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Experience</h3>
                </div>
                <p className="text-white/70 leading-relaxed">
                  Founded by veterans of leading tech companies and academic institutions, our leadership brings proven track records in scaling SaaS platforms and advancing research technology.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-teal-500 to-cyan-500 flex items-center justify-center">
                    <Globe className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Local Presence</h3>
                </div>
                <p className="text-white/70 leading-relaxed">
                  With offices in Poland (headquarters) and India (regional hub), we serve researchers across multiple time zones with culturally aware support.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Journey Timeline */}
      <section className="relative py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-white text-center mb-16">Our Journey</h2>
          
          {/* Animated Map View */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 mb-16 overflow-hidden">
            <svg viewBox="0 0 1200 400" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
              <defs>
                <linearGradient id="pathGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.5" />
                  <stop offset="50%" stopColor="#10b981" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#14b8a6" stopOpacity="0.5" />
                </linearGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>

              {/* Animated flowing path */}
              <path
                d="M 150 200 Q 350 120 450 200 Q 550 280 700 200 Q 850 120 1050 200"
                stroke="url(#pathGradient)"
                strokeWidth="4"
                fill="none"
                strokeLinecap="round"
                style={{
                  animation: 'flowPath 4s ease-in-out infinite',
                  filter: 'drop-shadow(0 0 10px rgba(59, 130, 246, 0.5))'
                }}
              />

              {/* Milestone 1 - Poland */}
              <g className="milestone" style={{ animation: 'slideIn 0.6s ease-out forwards' }}>
                <circle cx="150" cy="200" r="45" fill="url(#gradient1)" filter="url(#glow)" className="animate-pulse" style={{ animationDuration: '2s' }}/>
                <text x="150" y="210" textAnchor="middle" className="text-lg font-bold fill-white" fontSize="24">🇵🇱</text>
              </g>

              {/* Milestone 2 - Development */}
              <g className="milestone" style={{ animation: 'slideIn 0.8s ease-out forwards' }}>
                <circle cx="450" cy="200" r="45" fill="url(#gradient2)" filter="url(#glow)" className="animate-pulse" style={{ animationDuration: '2.2s', animationDelay: '0.3s' }}/>
                <text x="450" y="210" textAnchor="middle" className="text-lg font-bold fill-white" fontSize="24">⚙️</text>
              </g>

              {/* Milestone 3 - Growth */}
              <g className="milestone" style={{ animation: 'slideIn 1s ease-out forwards' }}>
                <circle cx="700" cy="200" r="45" fill="url(#gradient3)" filter="url(#glow)" className="animate-pulse" style={{ animationDuration: '2.4s', animationDelay: '0.6s' }}/>
                <text x="700" y="210" textAnchor="middle" className="text-lg font-bold fill-white" fontSize="24">📈</text>
              </g>

              {/* Milestone 4 - India */}
              <g className="milestone" style={{ animation: 'slideIn 1.2s ease-out forwards' }}>
                <circle cx="1050" cy="200" r="45" fill="url(#gradient4)" filter="url(#glow)" className="animate-pulse" style={{ animationDuration: '2.6s', animationDelay: '0.9s' }}/>
                <text x="1050" y="210" textAnchor="middle" className="text-lg font-bold fill-white" fontSize="24">🇮🇳</text>
              </g>

              {/* Labels */}
              <text x="150" y="280" textAnchor="middle" className="fill-white font-bold" fontSize="16">2019</text>
              <text x="450" y="280" textAnchor="middle" className="fill-white font-bold" fontSize="16">2020-22</text>
              <text x="700" y="280" textAnchor="middle" className="fill-white font-bold" fontSize="16">2023-24</text>
              <text x="1050" y="280" textAnchor="middle" className="fill-white font-bold" fontSize="16">2025</text>

              {/* Year labels bottom */}
              <text x="150" y="330" textAnchor="middle" className="fill-white/60 text-sm" fontSize="14">Founded</text>
              <text x="450" y="330" textAnchor="middle" className="fill-white/60 text-sm" fontSize="14">Development</text>
              <text x="700" y="330" textAnchor="middle" className="fill-white/60 text-sm" fontSize="14">Market Growth</text>
              <text x="1050" y="330" textAnchor="middle" className="fill-white/60 text-sm" fontSize="14">India Launch</text>

              {/* Animated dots traveling along path */}
              <g style={{ animation: 'travelPath 8s linear infinite' }}>
                <circle cx="150" cy="200" r="8" fill="#60a5fa" filter="url(#glow)" opacity="0.8"/>
              </g>

              <defs>
                <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3b82f6"/>
                  <stop offset="100%" stopColor="#06b6d4"/>
                </linearGradient>
                <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#06b6d4"/>
                  <stop offset="100%" stopColor="#14b8a6"/>
                </linearGradient>
                <linearGradient id="gradient3" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#14b8a6"/>
                  <stop offset="100%" stopColor="#10b981"/>
                </linearGradient>
                <linearGradient id="gradient4" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#10b981"/>
                  <stop offset="100%" stopColor="#059669"/>
                </linearGradient>
              </defs>
            </svg>
          </div>

          {/* Milestone Details */}
          <div className="space-y-8">
            {[
              {
                year: '2019',
                title: 'Founded in Poland',
                desc: 'Scopewit Sp.zo.o. established with a vision to transform academic publishing through technology.',
                location: 'Poland',
                color: 'from-blue-500 to-cyan-500',
                icon: '🚀'
              },
              {
                year: '2020-2022',
                title: 'Product Development',
                desc: 'Invested heavily in R&D to develop our AI-powered IEEE paper generation engine and platform infrastructure.',
                location: 'Poland',
                color: 'from-cyan-500 to-teal-500',
                icon: '⚙️'
              },
              {
                year: '2023-2024',
                title: 'Market Growth',
                desc: 'Reached 50,000+ active users across 150+ countries. Launched premium features and advanced collaboration tools.',
                location: 'Europe & Global',
                color: 'from-teal-500 to-emerald-500',
                icon: '📈'
              },
              {
                year: '2025',
                title: 'India Expansion',
                desc: 'Opened regional headquarters in India to better serve the rapidly growing Asian research market. Launched localized features.',
                location: 'India',
                color: 'from-emerald-500 to-green-500',
                icon: '🌏'
              }
            ].map((milestone, idx) => (
              <div key={idx} className="flex gap-8 items-start animate-fadeIn" style={{ animationDelay: `${idx * 0.2}s` }}>
                <div className={`relative flex flex-col items-center`}>
                  <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${milestone.color} flex items-center justify-center font-bold text-white text-3xl flex-shrink-0 shadow-lg transform transition-transform duration-300 hover:scale-110`}>
                    {milestone.icon}
                  </div>
                  {idx < 3 && (
                    <div className="w-1 h-24 bg-gradient-to-b from-white/30 to-white/5 mt-4 relative">
                      <div className="absolute inset-0 bg-gradient-to-b from-blue-500 to-emerald-500 w-full" style={{ animation: `fillDown 2s ease-in-out infinite`, animationDelay: `${idx * 0.5}s` }}/>
                    </div>
                  )}
                </div>
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 flex-1 hover:bg-white/10 transition transform hover:-translate-y-2">
                  <div className="flex flex-wrap items-center gap-4 mb-3">
                    <h3 className="text-2xl font-bold text-white">{milestone.year}</h3>
                    <span className="px-3 py-1 bg-white/10 rounded-full text-sm text-white/70">{milestone.location}</span>
                  </div>
                  <h4 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400 mb-2">
                    {milestone.title}
                  </h4>
                  <p className="text-white/70">{milestone.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl font-bold text-white mb-6">Join Our Community</h2>
          <p className="text-xl text-white/70 mb-8">
            Be part of a global movement revolutionizing academic publishing
          </p>
          <button className="group relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-emerald-600 rounded-full blur-lg opacity-75 group-hover:opacity-100 transition" />
            <div className="relative bg-gradient-to-r from-blue-600 to-emerald-600 text-white px-10 py-4 rounded-full font-semibold text-lg flex items-center justify-center gap-3 transform group-hover:scale-105 transition">
              Start Your Free Trial
              <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition" />
            </div>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative border-t border-white/10 bg-black/40 backdrop-blur-md py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
                 <h4 className="text-white font-bold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="/refunds" className="space-y-2 text-white/60 text-sm">Cancellation & Refunds</a></li>
                <li><a href="/shipping" className="space-y-2 text-white/60 text-sm">Shipping Policy</a></li>
                <li><a href="/gdpr" className="space-y-2 text-white/60 text-sm">GDPR Compliance</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-white/60 text-sm">
                <li>Email: info@scopewit.com</li>
                <li>Poland: Wroclaw</li>
                <li>India: Nagpur</li>
              </ul>
            </div>
             <div>
                 <h4 className="text-white font-bold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="/privacy" className="space-y-2 text-white/60 text-sm">Privacy Policy</a></li>
                <li><a href="/terms" className="space-y-2 text-white/60 text-sm">Terms of Service</a></li>
                <li><a href="/cookies" className="space-y-2 text-white/60 text-sm">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center">
            <p className="text-white/60 text-sm">
             © 2025 IEEE Assistant. All rights reserved.A product of scopewit sp.zo.o. technologies pvt. ltd.
            </p>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-20px) translateX(10px); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out forwards;
        }
      `}</style>
    </div>
  );
}